import { createTheme } from '@mui/material/styles';

declare module "@mui/material/styles" {
  interface TypographyVariants {
    pageTitle: React.CSSProperties;
    paperTitle: React.CSSProperties;
    category: React.CSSProperties;
    categoryActive: React.CSSProperties;
    controlTitle: React.CSSProperties;
    controlText: React.CSSProperties;
    sectionTitle: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties;
    paperTitle?: React.CSSProperties;
    category?: React.CSSProperties;
    categoryActive?: React.CSSProperties;
    controlTitle?: React.CSSProperties;
    controlText?: React.CSSProperties;
    sectionTitle?: React.CSSProperties;
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
    paperTitle: true;
    category: true;
    categoryActive: true;
    controlTitle: true;
    controlText: true;
    sectionTitle: true;
  }
}

const baseOptions = {
  typography: {
    fontFamily: '"Jost", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    sectionTitle: {
      fontSize: 14,
      fontWeight: 400,
      color: '#000000', // usato solo per il tema light, sovrascritto più sotto
    },
  },
  components: {
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
    },
    
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '3rem',
          textTransform: 'none',
          fontFamily: 'Jost',
          fontWeight: 700,
          fontSize: '14px',
          padding: '6px 18px',
          boxShadow: 'none',
          lineHeight: 1.8,
          '&:hover': {
            boxShadow: 'none',
          },
        },
        // niente override di colori/bordi sui varianti:
        // lasciamo che MUI gestisca outlined/contained come nel vecchio progetto
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: 'inherit',
          '&.Mui-selected': {
            backgroundColor: '#FFECB3',
            color: 'inherit',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          fontSize: '14px',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 4,
        },
        input: {
          fontWeight: 200,
          color: 'inherit',
        },
      },
    },
    
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-shrink': {
            color: (theme: any) => theme.palette.mode === 'dark' ? 'white' : theme.palette.text.primary,
          },
          '&:not(.MuiInputLabel-shrink)': {
            color: '#666666',
          },
          // lasciamo che MUI gestisca posizione/transform e anche font per avere il notch e il placeholder corretti
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          color: 'inherit',
          '&.MuiSelect-select.MuiSelect-selectEmpty': {
            color: '#666666',
          },
        },
        icon: {
          color: 'inherit',
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#41BB6C',
            '& + .MuiSwitch-track': {
              backgroundColor: '#41BB6C',
            },
          },
        },
        track: {
          backgroundColor: '#bdbdbd',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: '#F5B400', // colore della linea sotto la tab attiva
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', // niente maiuscole automatiche
          fontWeight: 600,
          color: 'inherit',
          '&.Mui-selected': {
            color: 'inherit',
          },
        },
      },
    },
    MuiFilledInput: {
      styleOverrides: {
        underline: {
          // stile normale
          '&:before': {
            borderBottom: '2px solid #000000', // teniamo il nero come stile di brand
          },
          // stile hover
          '&:hover:not(.Mui-disabled):before': {
            borderBottom: '2px solid #000000',
          },
          // stile quando focused
          '&:after': {
            borderBottom: '2px solid #F5B400', // colore underline focused
          },
        },
      },
    },
  },
} as const;

const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#F5B400',
    },
    secondary: {
      main: '#FED001',
    },
    background: {
      default: '#FFFFFF',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#000000',
      secondary: '#666666',
    },
  },
  ...baseOptions,
});

let theme = createTheme(lightTheme, {
  typography: {
    pageTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: lightTheme.palette.text.primary,
      marginLeft: 10,
      lineHeight: 2,
      textTransform: 'uppercase',
    },
    paperTitle: {
      fontSize: 16,
      fontWeight: 400,
      color: lightTheme.palette.text.primary,
    },
    category: {
      fontSize: 12,
      fontWeight: 400,
      color: '#000000',
      textTransform: 'uppercase',
      lineHeight: '15px',
    },
    categoryActive: {
      fontSize: 12,
      fontWeight: 600,
      color: lightTheme.palette.text.primary,
      textTransform: 'uppercase',
      lineHeight: '15px',
    },
    controlTitle: {
      fontSize: 14,
      color: lightTheme.palette.text.primary,
      fontWeight: 600,
    },
    controlText: {
      fontSize: 12,
      color: '#000000',
    },
  },
});

const darkThemeBase = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: '#F5B400',
    },
    secondary: {
      main: '#F5B400',
    },
    background: {
      default: '#212529',
      paper: '#212529',
    },
    text: {
      primary: '#FFFFFF',
      secondary: '#B0B0B0',
    },
  },
  ...baseOptions,
});

export const darkTheme = createTheme(darkThemeBase, {
  typography: {
    pageTitle: {
      fontSize: 14,
      fontWeight: 600,
      color: darkThemeBase.palette.text.primary,
      marginLeft: 10,
      lineHeight: 2,
      textTransform: 'uppercase',
    },
    paperTitle: {
      fontSize: 16,
      fontWeight: 400,
      color: darkThemeBase.palette.text.primary,
    },
    category: {
      fontSize: 12,
      fontWeight: 400,
      color: darkThemeBase.palette.text.primary,
      textTransform: 'uppercase',
      lineHeight: '15px',
    },
    categoryActive: {
      fontSize: 12,
      fontWeight: 600,
      color: darkThemeBase.palette.primary.main,
      textTransform: 'uppercase',
      lineHeight: '15px',
    },
    controlTitle: {
      fontSize: 14,
      color: darkThemeBase.palette.primary.main,
      fontWeight: 600,
    },
    controlText: {
      fontSize: 12,
      color: darkThemeBase.palette.text.primary,
    },
  },
});

export const getTheme = (darkMode: boolean) => (darkMode ? darkTheme : theme);

export default theme;
